/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useRef, useState } from 'react';
import { ReloadIcon } from '@radix-ui/react-icons';
import { useTranslation } from 'react-i18next';
import { FF_APP_FACEBOOK, FF_APP_PORTALS_INTEGRATION } from '@/shared/utils/feature-flag';
import { SectionCard } from '../SectionCard';
import { PublishDialog } from '../../PublishDialog';
import { PublicationsCenter } from '../../PublicationsCenter';
import type { PublicationsCenterHandle } from '../../PublicationsCenter';
import { Text, UnstyledButton } from '@/shared/ui';

interface PublicationsTabProps {
  propertyId: string;
  agencyId: string;
  propertyImages?: string[];
  /** Opens the publish dialog immediately on mount, e.g. when the user was
   * routed here specifically to publish (see PropertyDetailView's `?openPublish=1`). */
  autoOpenPublish?: boolean;
  /** Opens the property edit form; OLX's "Editar" uses it (saving updates the ad). */
  onEditProperty?: () => void;
}

/**
 * Renders the publications tab: where the listing is published, and the way
 * to publish it somewhere new.
 *
 * The portal rows come from `PublicationsCenter` rather than being redrawn
 * here. Each row carries editing, boosting and retrying a failed publish —
 * behaviour a flat list of portal names would have quietly dropped.
 *
 * @param props - The property, agency, images and the auto-open flag.
 * @returns The rendered tab.
 */
export const PublicationsTab: React.FC<PublicationsTabProps> = ({
  propertyId,
  agencyId,
  propertyImages = [],
  autoOpenPublish = false,
  onEditProperty,
}) => {
  const { t } = useTranslation('features/properties');
  const [showPublishDialog, setShowPublishDialog] = useState(autoOpenPublish);
  const [centerKey, setCenterKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const centerRef = useRef<PublicationsCenterHandle>(null);

  const isEnabled = FF_APP_FACEBOOK || FF_APP_PORTALS_INTEGRATION;

  async function handleRefresh() {
    setRefreshing(true);
    try {
      await centerRef.current?.refresh();
    } finally {
      setRefreshing(false);
    }
  }

  if (!isEnabled) {
    return (
      <SectionCard title={t('detailView.publications.title')}>
        <Text className="m-0 text-[length:var(--text-body-sm)] text-[var(--text-tertiary)]">
          {t('detailView.publications.notEnabled')}
        </Text>
      </SectionCard>
    );
  }

  return (
    <>
      <SectionCard
        title={t('detailView.publications.title')}
        data-testid="property-publications-card"
        titleAccessory={
          <div className="flex items-center gap-[var(--space-2)]">
            <UnstyledButton
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              aria-label={t('publications.refresh')}
              data-testid="publications-refresh-btn"
              className="inline-flex items-center justify-center gap-(--space-1) whitespace-nowrap max-md:size-9 text-[length:var(--text-meta)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] disabled:opacity-50"
            >
              <ReloadIcon width={14} height={14} className={refreshing ? 'animate-spin' : undefined} />
              {/* Icon-only below md: the label wrapped onto two lines beside the
                  publish button; aria-label keeps the name for assistive tech. */}
              <span className="max-md:hidden">
                {refreshing ? t('publications.refreshing') : t('publications.refresh')}
              </span>
            </UnstyledButton>
            <UnstyledButton
              type="button"
              onClick={() => setShowPublishDialog(true)}
              data-testid="property-publish-btn"
              className="h-[34px] shrink-0 whitespace-nowrap rounded-(--radius-md) border border-solid border-[var(--border-medium)] bg-[var(--surface-primary)] px-[var(--space-3)] text-[length:var(--text-meta)] [font-weight:var(--font-semibold)] text-[var(--text-primary)] hover:bg-[var(--surface-hover)]"
            >
              {t('detailView.publications.publish')}
            </UnstyledButton>
          </div>
        }
      >
        <PublicationsCenter
          key={centerKey}
          ref={centerRef}
          propertyId={propertyId}
          agencyId={agencyId}
          propertyImages={propertyImages}
          inline
          onClose={() => {}}
          onEditProperty={onEditProperty}
        />
      </SectionCard>

      {showPublishDialog && (
        <PublishDialog
          propertyId={propertyId}
          agencyId={agencyId}
          propertyImages={propertyImages}
          onClose={() => setShowPublishDialog(false)}
          onPublished={() => setCenterKey((k) => k + 1)}
        />
      )}
    </>
  );
};
